import { Box, ButtonBase } from "@mui/material";
import { AddCircleOutline } from "@mui/icons-material";
import { StriimTypography, StriimButton } from "@striim/striim-ui-v2";
import React, { useCallback, useMemo, useState } from "react";
import { useReactFlow } from "reactflow";
import { SUPPORTED_NEW_ADD_NODE_OPTIONS, AI_NODES } from "./supported-nodes";
import { streamNodeStyles } from "../nodes/styles/stream-node.styles";
import { useAppDispatch, useAppSelector } from "src/stores/redux/hooks";
import { getNodes, getEdges, getCurrentLayout } from "../helpers/selectors";
import {
    setEdgesAction,
    setIsRightSidebarOpenAction,
    setNodesAction,
    setSelectedNodeAction
} from "src/stores/redux/features/flowdesigner/flowdesigner.slice";
import { NodeIcon } from "../nodes/node.commons";
import { handleConnect } from "./add-new-node.utils";
import { SupportedNewAddNodeOption } from "./supported-nodes";
import allowedTargetNodeTypesResolver from "app/components/flow/designer/allowedTargetNodeTypesResolver";
import utils from "core/utils";

type SelectOption = { label: string; value: string };

type AddNewNodeProps = {
    nodeId: string;
    data: Record<string, any>;
};

const StreamNodeAdd = ({ nodeId, data }: AddNewNodeProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const dispatch = useAppDispatch();
    const { setCenter } = useReactFlow();

    const nodes = useAppSelector(getNodes);
    const edges = useAppSelector(getEdges);
    const currentLayout = useAppSelector(getCurrentLayout);
    const [selectedOption, setSelectedOption] = useState<SelectOption | null>(null);

    const supportedNewAddNodeOptions = useMemo(() => {
        return SUPPORTED_NEW_ADD_NODE_OPTIONS.filter(option => {
            return allowedTargetNodeTypesResolver.getAllowedChildren(data.metaObject.type).includes(option.value);
        });
    }, [data.metaObject.type]);

    const showAINodes = useMemo(() => data.metaObject.type === utils.ENTITIES.STREAM, [data.metaObject.type]);

    const parentNode = useMemo(() => nodes.find(node => node.id === nodeId), [nodes, nodeId]);

    const handleListItemClick = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>, option: SupportedNewAddNodeOption) => {
            setSelectedOption({ label: option.label, value: option.value });
            e.stopPropagation();
        },
        []
    );

    const onConnect = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            if (!selectedOption || !parentNode) {
                return;
            }

            const result = handleConnect({
                selectedOption,
                parentNode,
                nodeId,
                data,
                nodes,
                edges,
                currentLayout
            });

            if (!result) {
                return;
            }

            const { layoutedNodes, layoutedEdges, newNodeId } = result;
            const newNode = layoutedNodes.find(node => node.id === newNodeId);

            dispatch(setNodesAction(layoutedNodes));
            dispatch(setEdgesAction(layoutedEdges));
            setTimeout(() => {
                dispatch(setSelectedNodeAction(newNode || null));
                dispatch(setIsRightSidebarOpenAction(true));
                if (newNode?.position) {
                    setCenter(newNode.position.x, newNode.position.y);
                }
            });
            setSelectedOption(null);
            e.stopPropagation();
            e.preventDefault();
        },
        [currentLayout, data, dispatch, edges, nodeId, nodes, parentNode, selectedOption, setCenter]
    );

    if (supportedNewAddNodeOptions.length === 0) {
        return null;
    }
    return (
        <Box sx={streamNodeStyles.addNodeContainer}>
            <Box
                sx={streamNodeStyles.addIconWrapper}
                data-test-id="add-node-button"
                onClick={e => {
                    setIsOpen(prev => !prev);
                    e.stopPropagation();
                }}
            >
                <AddCircleOutline sx={streamNodeStyles.addIcon} />
            </Box>

            <Box
                sx={streamNodeStyles.addNodePanel}
                data-test-id="add-node-panel"
                onClick={e => e.stopPropagation()}
                hidden={!isOpen}
            >
                <Box sx={streamNodeStyles.availableNodesList}>
                    {showAINodes && (
                        <Box mb={2} data-test-id="add-node-ai-section">
                            <StriimTypography variant="caption4" sx={streamNodeStyles.availableNodesListTitle}>
                                AI Agents
                            </StriimTypography>
                            {AI_NODES.map(option => {
                                const isSelected =
                                    selectedOption?.label === option.label && selectedOption?.value === option.value;

                                return (
                                    <ButtonBase
                                        key={option.value}
                                        data-test-id={`add-node-option-${option.label}`}
                                        sx={streamNodeStyles.availableNodesListItem(isSelected)}
                                        onClick={e => handleListItemClick(e, option)}
                                    >
                                        <Box sx={streamNodeStyles.availableNodesListItemContent}>
                                            <Box sx={streamNodeStyles.availableNodesListItemIcon}>
                                                <NodeIcon data={option.iconData} />
                                            </Box>
                                            <StriimTypography
                                                variant="caption"
                                                sx={streamNodeStyles.availableNodesListItemLabel}
                                            >
                                                {option.label}
                                            </StriimTypography>
                                        </Box>
                                    </ButtonBase>
                                );
                            })}
                        </Box>
                    )}
                    <StriimTypography variant="caption4" sx={streamNodeStyles.availableNodesListTitle}>
                        Available components
                    </StriimTypography>
                    {supportedNewAddNodeOptions.map(option => {
                        const isSelected =
                            selectedOption?.label === option.label && selectedOption?.value === option.value;

                        return (
                            <ButtonBase
                                key={option.value}
                                data-test-id={`add-node-option-${option.label}`}
                                sx={streamNodeStyles.availableNodesListItem(isSelected)}
                                onClick={e => handleListItemClick(e, option)}
                            >
                                <Box sx={streamNodeStyles.availableNodesListItemContent}>
                                    <Box sx={streamNodeStyles.availableNodesListItemIcon}>
                                        <NodeIcon data={option.iconData} />
                                    </Box>
                                    <StriimTypography
                                        variant="caption"
                                        sx={streamNodeStyles.availableNodesListItemLabel}
                                    >
                                        {option.label}
                                    </StriimTypography>
                                </Box>
                            </ButtonBase>
                        );
                    })}
                    <Box sx={streamNodeStyles.connectButtonWrapper}>
                        <StriimButton
                            variant="text"
                            size="small"
                            data-test-id="add-node-cancel-btn"
                            onClick={() => setIsOpen(false)}
                        >
                            Cancel
                        </StriimButton>
                        <StriimButton
                            variant="primary"
                            size="small"
                            data-test-id="add-node-connect-btn"
                            onClick={onConnect}
                            disabled={!selectedOption}
                        >
                            Connect
                        </StriimButton>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default StreamNodeAdd;
